import { useState, useRef, useCallback, useEffect } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import type { CanvasStep, StepType } from './types';
import { defaultStep, stepsToApi, stepsFromApi, STEP_META } from './types';
import { WorkflowNode } from './WorkflowNode';
import { StepConfigPanel } from './StepConfigPanel';
import styles from './WorkflowCanvas.module.css';

interface Tool { toolName: string; displayName: string; inputSchema?: any }
interface Skill { id: string; name: string; inputSchema?: any }

interface WorkflowCanvasProps {
  /** Raw steps from the API (unknown[]) */
  initialSteps: unknown[];
  initialTriggerConfig?: any;
  initialAbortListeners?: any[];
  initialMaxExecutionTime?: number | null;
  availableTools: Tool[];
  availableSkills: Skill[];
  availableConnectors?: any[];
  /** Called when user clicks Save — receives steps, triggerConfig, abortListeners, and maxExecutionTime */
  onSave: (steps: object[], triggerConfig: any, abortListeners: any[], maxExecutionTime: number | null) => void;
  isSaving?: boolean;
}

/** Helper: Trace reachable steps from a start ID following onSuccess transitions */
function getReachable(steps: CanvasStep[], startId: string | undefined): Set<string> {
  const reachable = new Set<string>();
  if (!startId) return reachable;
  const queue = [startId];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    if (reachable.has(curr)) continue;
    const step = steps.find(s => s.id === curr);
    if (!step) continue;
    reachable.add(curr);
    if (step.type === 'condition') {
      if (step.onSuccess) queue.push(step.onSuccess);
      if (step.onFailure) queue.push(step.onFailure);
    } else {
      if (step.onSuccess) {
        queue.push(step.onSuccess);
      } else {
        const idx = steps.findIndex(s => s.id === step.id);
        if (idx !== -1 && idx + 1 < steps.length) {
          queue.push(steps[idx + 1].id);
        }
      }
    }
  }
  return reachable;
}

/** Helper: Partition flat steps array into execution tracks */
function partitionSteps(steps: CanvasStep[]) {
  const condIdx = steps.findIndex(s => s.type === 'condition');
  if (condIdx === -1) {
    return {
      before: steps,
      condition: null,
      trueBranch: [] as CanvasStep[],
      falseBranch: [] as CanvasStep[],
      after: [] as CanvasStep[]
    };
  }
  const before = steps.slice(0, condIdx);
  const condition = steps[condIdx];

  const successReachable = getReachable(steps, condition.onSuccess);
  const failureReachable = getReachable(steps, condition.onFailure);

  // Reunion/intersection points where the branches merge back
  const reunionIds = new Set<string>();
  for (const id of successReachable) {
    if (failureReachable.has(id)) {
      reunionIds.add(id);
    }
  }

  const trueBranch: CanvasStep[] = [];
  const falseBranch: CanvasStep[] = [];
  const after: CanvasStep[] = [];

  steps.slice(condIdx + 1).forEach(step => {
    if (reunionIds.has(step.id)) {
      after.push(step);
    } else if (successReachable.has(step.id)) {
      trueBranch.push(step);
    } else if (failureReachable.has(step.id)) {
      falseBranch.push(step);
    } else {
      after.push(step);
    }
  });

  return { before, condition, trueBranch, falseBranch, after };
}

/** Helper: Rebuild the flat steps array and assign onSuccess / onFailure pointers */
function rebuildSteps(parts: ReturnType<typeof partitionSteps>): CanvasStep[] {
  const { before, condition, trueBranch, falseBranch, after } = parts;
  if (!condition) {
    const all = [...before, ...after];
    return all.map(s => ({ ...s, onSuccess: undefined, onFailure: undefined }));
  }

  const nextAfterId = after.length > 0 ? after[0].id : 'end';

  const updatedTrueBranch = trueBranch.map((s, idx) => {
    const isLast = idx === trueBranch.length - 1;
    return {
      ...s,
      onSuccess: isLast ? nextAfterId : trueBranch[idx + 1].id,
      onFailure: undefined,
    };
  });

  const updatedFalseBranch = falseBranch.map((s, idx) => {
    const isLast = idx === falseBranch.length - 1;
    return {
      ...s,
      onSuccess: isLast ? nextAfterId : falseBranch[idx + 1].id,
      onFailure: undefined,
    };
  });

  const updatedCondition = {
    ...condition,
    onSuccess: updatedTrueBranch.length > 0 ? updatedTrueBranch[0].id : nextAfterId,
    onFailure: updatedFalseBranch.length > 0 ? updatedFalseBranch[0].id : nextAfterId,
  };

  const updatedBefore = before.map(s => ({ ...s, onSuccess: undefined, onFailure: undefined }));
  const updatedAfter = after.map((s, idx) => {
    const isLast = idx === after.length - 1;
    return {
      ...s,
      onSuccess: isLast ? 'end' : after[idx + 1].id,
      onFailure: undefined,
    };
  });

  return [...updatedBefore, updatedCondition, ...updatedTrueBranch, ...updatedFalseBranch, ...updatedAfter];
}

/** Spacer divider between nodes (connections are drawn by SVG) */
function Arrow() {
  return (
    <div className={styles.arrowSegment}>
      <div className={styles.arrowLine} />
    </div>
  );
}

export function WorkflowCanvas({
  initialSteps,
  initialTriggerConfig,
  initialAbortListeners,
  initialMaxExecutionTime,
  availableTools,
  availableSkills,
  availableConnectors = [],
  onSave,
  isSaving = false,
}: WorkflowCanvasProps) {
  const [steps, setSteps] = useState<CanvasStep[]>(() => stepsFromApi(initialSteps));
  const [triggerConfig, setTriggerConfig] = useState<any>(() => initialTriggerConfig ?? { type: 'manual' });
  const [abortListeners, setAbortListeners] = useState<any[]>(() => initialAbortListeners ?? []);
  const [maxExecutionTime, setMaxExecutionTime] = useState<number | null>(() => initialMaxExecutionTime ?? null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Active insert zone tracking state for selecting step type
  const [activeInsertZone, setActiveInsertZone] = useState<{
    id: string | 'end';
    branch: 'true' | 'false' | 'main';
    x: number;
    y: number;
  } | null>(null);

  const [coords, setCoords] = useState<Record<string, { x: number; y: number; w: number; h: number }>>({});
  const canvasRef = useRef<HTMLDivElement>(null);

  // Re-parse if parent provides new initial values (e.g. after query refresh)
  useEffect(() => {
    setSteps(stepsFromApi(initialSteps));
  }, [JSON.stringify(initialSteps)]); // eslint-disable-line

  useEffect(() => {
    setTriggerConfig(initialTriggerConfig ?? { type: 'manual' });
  }, [JSON.stringify(initialTriggerConfig)]);

  useEffect(() => {
    setAbortListeners(initialAbortListeners ?? []);
  }, [JSON.stringify(initialAbortListeners)]);

  useEffect(() => {
    setMaxExecutionTime(initialMaxExecutionTime ?? null);
  }, [initialMaxExecutionTime]);

  // ── Auto-save logic to database ──────────────────────────────────────
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    const currentSerializedSteps = JSON.stringify(stepsToApi(steps));
    const initialSerializedSteps = JSON.stringify(initialSteps);
    const currentTrigger = JSON.stringify(triggerConfig);
    const initialTrigger = JSON.stringify(initialTriggerConfig ?? { type: 'manual' });
    const currentAbort = JSON.stringify(abortListeners);
    const initialAbort = JSON.stringify(initialAbortListeners ?? []);
    const currentMaxExec = maxExecutionTime;
    const initialMaxExec = initialMaxExecutionTime ?? null;

    if (
      currentSerializedSteps === initialSerializedSteps &&
      currentTrigger === initialTrigger &&
      currentAbort === initialAbort &&
      currentMaxExec === initialMaxExec
    ) {
      return;
    }

    const t = setTimeout(() => {
      onSave(stepsToApi(steps), triggerConfig, abortListeners, maxExecutionTime);
    }, 1500); // 1.5s debounce
    return () => clearTimeout(t);
  }, [steps, triggerConfig, abortListeners, maxExecutionTime, initialSteps, initialTriggerConfig, initialAbortListeners, initialMaxExecutionTime, onSave]);

  // Close insert menu when clicking outside
  useEffect(() => {
    if (!activeInsertZone) return;
    const handleOutsideClick = () => {
      setActiveInsertZone(null);
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, [activeInsertZone]);

  const selectedStep = steps.find((s) => s.id === selectedId) ?? null;
  const toolNames = availableTools.map((t) => t.toolName);
  const skillNames = availableSkills.map((s) => s.name);

  // Partitioned representation for layout
  const parts = partitionSteps(steps);
  const { before, condition, trueBranch, falseBranch, after } = parts;

  // ── Coordinates Tracking for SVG connections ─────────────────────────
  const updateCoords = useCallback(() => {
    const container = canvasRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const newCoords: typeof coords = {};

    const startEl = container.querySelector('#wf-node-start');
    if (startEl) {
      const rect = startEl.getBoundingClientRect();
      newCoords['start'] = {
        x: rect.left - containerRect.left + container.scrollLeft,
        y: rect.top - containerRect.top + container.scrollTop,
        w: rect.width,
        h: rect.height,
      };
    }

    const endEl = container.querySelector('#wf-node-end');
    if (endEl) {
      const rect = endEl.getBoundingClientRect();
      newCoords['end'] = {
        x: rect.left - containerRect.left + container.scrollLeft,
        y: rect.top - containerRect.top + container.scrollTop,
        w: rect.width,
        h: rect.height,
      };
    }

    steps.forEach((step) => {
      const el = container.querySelector(`#wf-node-${step.id}`);
      if (el) {
        const rect = el.getBoundingClientRect();
        newCoords[step.id] = {
          x: rect.left - containerRect.left + container.scrollLeft,
          y: rect.top - containerRect.top + container.scrollTop,
          w: rect.width,
          h: rect.height,
        };
      }
    });

    const truePlaceholder = container.querySelector('#wf-placeholder-true');
    if (truePlaceholder) {
      const rect = truePlaceholder.getBoundingClientRect();
      newCoords['placeholder-true'] = {
        x: rect.left - containerRect.left + container.scrollLeft,
        y: rect.top - containerRect.top + container.scrollTop,
        w: rect.width,
        h: rect.height,
      };
    }

    const falsePlaceholder = container.querySelector('#wf-placeholder-false');
    if (falsePlaceholder) {
      const rect = falsePlaceholder.getBoundingClientRect();
      newCoords['placeholder-false'] = {
        x: rect.left - containerRect.left + container.scrollLeft,
        y: rect.top - containerRect.top + container.scrollTop,
        w: rect.width,
        h: rect.height,
      };
    }

    setCoords(newCoords);
  }, [steps]);

  useEffect(() => {
    updateCoords();
    window.addEventListener('resize', updateCoords);
    const t = setTimeout(updateCoords, 150);
    return () => {
      window.removeEventListener('resize', updateCoords);
      clearTimeout(t);
    };
  }, [updateCoords, steps, selectedId]);

  // ── Step CRUD ────────────────────────────────────────────────────────
  const addStep = useCallback((type: StepType, afterIndex?: number) => {
    const step = defaultStep(type);
    setSteps((prev) => {
      if (afterIndex !== undefined) {
        const next = [...prev];
        next.splice(afterIndex + 1, 0, step);
        return next;
      }
      return [...prev, step];
    });
    setSelectedId(step.id);
  }, []);

  const addStepToBranch = useCallback((type: StepType, branch: 'true' | 'false') => {
    const step = defaultStep(type);
    setSteps((prev) => {
      const parts = partitionSteps(prev);
      if (branch === 'true') {
        parts.trueBranch.push(step);
      } else {
        parts.falseBranch.push(step);
      }
      return rebuildSteps(parts);
    });
    setSelectedId(step.id);
  }, []);

  const insertStepBefore = useCallback((insertId: string, _branch: 'true' | 'false' | 'main', type: StepType) => {
    const step = defaultStep(type);
    setSteps((prev) => {
      const parts = partitionSteps(prev);
      if (parts.before.some(s => s.id === insertId)) {
        const idx = parts.before.findIndex(s => s.id === insertId);
        parts.before.splice(idx, 0, step);
      } else if (parts.trueBranch.some(s => s.id === insertId)) {
        const idx = parts.trueBranch.findIndex(s => s.id === insertId);
        parts.trueBranch.splice(idx, 0, step);
      } else if (parts.falseBranch.some(s => s.id === insertId)) {
        const idx = parts.falseBranch.findIndex(s => s.id === insertId);
        parts.falseBranch.splice(idx, 0, step);
      } else if (parts.after.some(s => s.id === insertId)) {
        const idx = parts.after.findIndex(s => s.id === insertId);
        parts.after.splice(idx, 0, step);
      } else if (parts.condition && parts.condition.id === insertId) {
        parts.before.push(step);
      }
      return rebuildSteps(parts);
    });
    setSelectedId(step.id);
  }, []);

  const removeStep = useCallback((id: string) => {
    setSteps((prev) => {
      const filtered = prev.filter((s) => s.id !== id);
      const parts = partitionSteps(filtered);
      return rebuildSteps(parts);
    });
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const updateStep = useCallback((id: string, patch: Partial<CanvasStep>) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, []);



  // ── Save ─────────────────────────────────────────────────────────────
  function handleSave() {
    onSave(stepsToApi(steps), triggerConfig, abortListeners, maxExecutionTime);
  }

  // ── Dismiss panel when clicking canvas background ────────────────────
  function handleCanvasBgClick(e: React.MouseEvent) {
    if (e.target === canvasRef.current) {
      setSelectedId(null);
      setActiveInsertZone(null);
    }
  }

  // ── Trigger Insert Step Menu ─────────────────────────────────────────
  const handleInsertBtnClick = (e: React.MouseEvent<HTMLButtonElement>, id: string | 'end', branch: 'true' | 'false' | 'main') => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const container = canvasRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();

    setActiveInsertZone({
      id,
      branch,
      x: rect.left - containerRect.left + container.scrollLeft + rect.width / 2,
      y: rect.top - containerRect.top + container.scrollTop + rect.height + 6,
    });
  };

  // ── Insert Button Zone Component ─────────────────────────────────────
  function InsertButtonZone({ id, branch }: { id: string | 'end'; branch: 'true' | 'false' | 'main' }) {
    return (
      <div className={styles.arrowSegment}>
        <div className={styles.arrowLine} />
        <button
          className={styles.insertBtn}
          onClick={(e) => handleInsertBtnClick(e, id, branch)}
          title="Insert step here"
          type="button"
        >
          +
        </button>
        <div className={styles.arrowLine} />
      </div>
    );
  }

  // ── Connection lines ──────────────────────────────────────────────────
  const connections: Array<{ fromId: string; toId: string; type: 'default' | 'success' | 'failure' | 'loop'; label?: string }> = [];

  if (steps.length > 0) {
    connections.push({ fromId: 'start', toId: steps[0].id, type: 'default' });
  } else {
    connections.push({ fromId: 'start', toId: 'end', type: 'default' });
  }

  steps.forEach((step, i) => {
    if (step.type === 'condition') {
      if (step.onSuccess && steps.some(s => s.id === step.onSuccess)) {
        connections.push({ fromId: step.id, toId: step.onSuccess, type: 'success', label: 'TRUE' });
      } else {
        connections.push({ fromId: step.id, toId: 'placeholder-true', type: 'success', label: 'TRUE' });
      }

      if (step.onFailure && steps.some(s => s.id === step.onFailure)) {
        connections.push({ fromId: step.id, toId: step.onFailure, type: 'failure', label: 'FALSE' });
      } else {
        connections.push({ fromId: step.id, toId: 'placeholder-false', type: 'failure', label: 'FALSE' });
      }
    } else if (step.type === 'loop') {
      if (step.targetStepId) {
        connections.push({ fromId: step.id, toId: step.targetStepId, type: 'loop', label: 'LOOP' });
      }
      const nextId = step.onSuccess || (i + 1 < steps.length ? steps[i + 1].id : 'end');
      connections.push({ fromId: step.id, toId: nextId, type: 'default' });
    } else {
      const nextId = step.onSuccess || (i + 1 < steps.length ? steps[i + 1].id : 'end');
      connections.push({ fromId: step.id, toId: nextId, type: 'default' });
    }
  });

  return (
    <>
      {/* Canvas wrapper */}
      <div className={styles.canvasWrapper}>
        {/* Save button inside canvas top-right */}
        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
            {steps.length} step{steps.length !== 1 ? 's' : ''}
          </span>
          <span style={{
            fontSize: '0.75rem',
            color: isSaving ? 'var(--color-brand)' : '#22c55e',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}>
            {isSaving ? (
              <>
                <Loader2 size={12} className={styles.spinIcon} />
                Autosaving…
              </>
            ) : (
              <>
                <CheckCircle2 size={12} />
                Autosaved
              </>
            )}
          </span>
          <button
            id="wf-canvas-save-btn"
            onClick={handleSave}
            disabled={isSaving}
            style={{
              background: 'var(--color-surface-2)',
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text)',
              padding: '0.4rem 1rem',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              opacity: isSaving ? 0.7 : 1,
              transition: 'all 0.15s',
            }}
          >
            Save Now
          </button>
        </div>

        {/* Canvas board */}
        <div
          className={styles.canvas}
          ref={canvasRef}
          onClick={handleCanvasBgClick}
          role="region"
          aria-label="Workflow canvas"
        >
          {/* SVG Overlay representing connections */}
          <svg className={styles.connectorSvg}>
            <defs>
              <marker
                id="arrow-default"
                viewBox="0 0 10 10"
                refX="6"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M 0 1 L 10 5 L 0 9 z" fill="rgba(99, 102, 241, 0.4)" />
              </marker>
              <marker
                id="arrow-success"
                viewBox="0 0 10 10"
                refX="6"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M 0 1 L 10 5 L 0 9 z" fill="#22c55e" />
              </marker>
              <marker
                id="arrow-failure"
                viewBox="0 0 10 10"
                refX="6"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M 0 1 L 10 5 L 0 9 z" fill="#ef4444" />
              </marker>
              <marker
                id="arrow-loop"
                viewBox="0 0 10 10"
                refX="6"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M 0 1 L 10 5 L 0 9 z" fill="#8b5cf6" />
              </marker>
            </defs>
            {connections.map(({ fromId, toId, type, label }) => {
              const from = coords[fromId];
              const to = coords[toId];
              if (!from || !to) return null;

              const p1 = { x: from.x + from.w, y: from.y + from.h / 2 };
              const p2 = { x: to.x, y: to.y + to.h / 2 };

              let d = '';
              let strokeColor = 'rgba(99, 102, 241, 0.4)';
              let marker = 'arrow-default';

              if (type === 'success') {
                strokeColor = '#22c55e';
                marker = 'arrow-success';
              } else if (type === 'failure') {
                strokeColor = '#ef4444';
                marker = 'arrow-failure';
              } else if (type === 'loop') {
                strokeColor = '#8b5cf6';
                marker = 'arrow-loop';
              }

              const dx = p2.x - p1.x;

              if (type === 'loop' && p2.x <= p1.x) {
                const hDiff = Math.abs(p1.x - p2.x);
                const curveDepth = Math.max(50, 30 + hDiff * 0.12);
                d = `M ${p1.x} ${p1.y} 
                     C ${p1.x + 35} ${p1.y + 10}, ${p1.x + 35} ${p1.y + curveDepth}, ${p1.x} ${p1.y + curveDepth} 
                     L ${p2.x} ${p2.y + curveDepth} 
                     C ${p2.x - 35} ${p2.y + curveDepth}, ${p2.x - 35} ${p2.y + 10}, ${p2.x} ${p2.y}`;
              } else if (dx > 160 && (type === 'success' || type === 'failure')) {
                const curveHeight = Math.max(45, 20 + dx * 0.08);
                d = `M ${p1.x} ${p1.y} 
                     C ${p1.x + 25} ${p1.y - 10}, ${p1.x + 25} ${p1.y - curveHeight}, ${(p1.x + p2.x) / 2} ${p1.y - curveHeight} 
                     C ${p2.x - 25} ${p2.y - curveHeight}, ${p2.x - 25} ${p2.y - 10}, ${p2.x} ${p2.y}`;
              } else {
                const controlX = p1.x + dx / 2;
                d = `M ${p1.x} ${p1.y} C ${controlX} ${p1.y}, ${controlX} ${p2.y}, ${p2.x} ${p2.y}`;
              }

              return (
                <g key={`${fromId}-${toId}-${type}`}>
                  <path
                    id={`path-${fromId}-${toId}-${type}`}
                    d={d}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={type === 'default' ? 2 : 2.5}
                    strokeDasharray={type === 'loop' ? '4 4' : undefined}
                    markerEnd={`url(#${marker})`}
                  />
                  {label && (
                    <text dy="-4">
                      <textPath
                        href={`#path-${fromId}-${toId}-${type}`}
                        startOffset={type === 'loop' ? '30%' : '50%'}
                        textAnchor="middle"
                        fill={strokeColor}
                        style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.05em' }}
                      >
                        {label}
                      </textPath>
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Conditional Rendering based on Branching */}
          {condition ? (
            <div className={styles.canvasRow}>
              {/* START terminal */}
              <div
                id="wf-node-start"
                className={`${styles.terminalNode} ${styles.startNode} ${selectedId === 'trigger' ? styles.selectedTrigger : ''}`}
                onClick={() => setSelectedId(selectedId === 'trigger' ? null : 'trigger')}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700 }}>START</span>
                  {triggerConfig?.type && triggerConfig.type !== 'manual' && (
                    <span style={{
                      fontSize: '0.5rem',
                      background: 'rgba(255, 255, 255, 0.25)',
                      padding: '1px 4px',
                      borderRadius: '3px',
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}>
                      {triggerConfig.type}
                    </span>
                  )}
                </div>
              </div>

              {/* Steps before condition */}
              {before.map((step) => (
                <div key={step.id} style={{ display: 'flex', alignItems: 'center' }}>
                  <InsertButtonZone id={step.id} branch="main" />
                  <WorkflowNode
                    step={step}
                    isSelected={selectedId === step.id}
                    availableToolNames={toolNames}
                    availableSkillNames={skillNames}
                    onClick={() => setSelectedId(step.id === selectedId ? null : step.id)}
                    onDelete={() => removeStep(step.id)}
                  />
                  <Arrow />
                </div>
              ))}

              {/* Insert button before condition node */}
              <InsertButtonZone id={condition.id} branch="main" />

              {/* Condition node */}
              <WorkflowNode
                step={condition}
                isSelected={selectedId === condition.id}
                availableToolNames={toolNames}
                availableSkillNames={skillNames}
                onClick={() => setSelectedId(condition.id === selectedId ? null : condition.id)}
                onDelete={() => removeStep(condition.id)}
              />

              {/* Parallel branch split tracks */}
              <div className={styles.branchesContainer}>
                {/* TRUE branch track */}
                <div className={styles.branchTrack}>
                  <div className={styles.branchLabel} style={{ color: '#22c55e' }}>TRUE</div>
                  <div className={styles.branchSteps}>
                    {trueBranch.length === 0 ? (
                      <div
                        id="wf-placeholder-true"
                        className={styles.placeholderCard}
                        onClick={() => addStepToBranch('tool', 'true')}
                      >
                        + Add True Step
                      </div>
                    ) : (
                      trueBranch.map((step, idx) => (
                        <div key={step.id} style={{ display: 'flex', alignItems: 'center' }}>
                          <InsertButtonZone id={step.id} branch="true" />
                          <WorkflowNode
                            step={step}
                            isSelected={selectedId === step.id}
                            availableToolNames={toolNames}
                            availableSkillNames={skillNames}
                            onClick={() => setSelectedId(step.id === selectedId ? null : step.id)}
                            onDelete={() => removeStep(step.id)}
                          />
                          {idx === trueBranch.length - 1 && <InsertButtonZone id="end" branch="true" />}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* FALSE branch track */}
                <div className={styles.branchTrack}>
                  <div className={styles.branchLabel} style={{ color: '#ef4444' }}>FALSE</div>
                  <div className={styles.branchSteps}>
                    {falseBranch.length === 0 ? (
                      <div
                        id="wf-placeholder-false"
                        className={styles.placeholderCard}
                        onClick={() => addStepToBranch('tool', 'false')}
                      >
                        + Add False Step
                      </div>
                    ) : (
                      falseBranch.map((step, idx) => (
                        <div key={step.id} style={{ display: 'flex', alignItems: 'center' }}>
                          <InsertButtonZone id={step.id} branch="false" />
                          <WorkflowNode
                            step={step}
                            isSelected={selectedId === step.id}
                            availableToolNames={toolNames}
                            availableSkillNames={skillNames}
                            onClick={() => setSelectedId(step.id === selectedId ? null : step.id)}
                            onDelete={() => removeStep(step.id)}
                          />
                          {idx === falseBranch.length - 1 && <InsertButtonZone id="end" branch="false" />}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Steps after reunion */}
              {after.map((step) => (
                <div key={step.id} style={{ display: 'flex', alignItems: 'center' }}>
                  <InsertButtonZone id={step.id} branch="main" />
                  <WorkflowNode
                    step={step}
                    isSelected={selectedId === step.id}
                    availableToolNames={toolNames}
                    availableSkillNames={skillNames}
                    onClick={() => setSelectedId(step.id === selectedId ? null : step.id)}
                    onDelete={() => removeStep(step.id)}
                  />
                  <Arrow />
                </div>
              ))}

              {/* Insert zone at the end */}
              <InsertButtonZone id="end" branch="main" />

              {/* END terminal */}
              <div
                id="wf-node-end"
                className={`${styles.terminalNode} ${styles.endNode} ${selectedId === 'end' ? styles.selectedEnd : ''}`}
                onClick={() => setSelectedId(selectedId === 'end' ? null : 'end')}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700 }}>END</span>
                  {maxExecutionTime != null && (
                    <span style={{
                      fontSize: '0.5rem',
                      background: 'rgba(255,255,255,0.25)',
                      padding: '1px 4px',
                      borderRadius: '3px',
                      fontWeight: 700,
                    }}>
                      {maxExecutionTime}s
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Linear flow rendering
            <div className={styles.canvasRow}>
              {/* START terminal */}
              <div
                id="wf-node-start"
                className={`${styles.terminalNode} ${styles.startNode} ${selectedId === 'trigger' ? styles.selectedTrigger : ''}`}
                onClick={() => setSelectedId(selectedId === 'trigger' ? null : 'trigger')}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700 }}>START</span>
                  {triggerConfig?.type && triggerConfig.type !== 'manual' && (
                    <span style={{
                      fontSize: '0.5rem',
                      background: 'rgba(255, 255, 255, 0.25)',
                      padding: '1px 4px',
                      borderRadius: '3px',
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}>
                      {triggerConfig.type}
                    </span>
                  )}
                </div>
              </div>

              {steps.map((step, idx) => (
                <div key={step.id} style={{ display: 'flex', alignItems: 'center' }}>
                  <InsertButtonZone id={step.id} branch="main" />
                  <WorkflowNode
                    step={step}
                    isSelected={selectedId === step.id}
                    availableToolNames={toolNames}
                    availableSkillNames={skillNames}
                    onClick={() => setSelectedId(step.id === selectedId ? null : step.id)}
                    onDelete={() => removeStep(step.id)}
                  />
                  {idx === steps.length - 1 && <InsertButtonZone id="end" branch="main" />}
                </div>
              ))}

              {steps.length === 0 && <InsertButtonZone id="end" branch="main" />}

              {/* END terminal */}
              <div
                id="wf-node-end"
                className={`${styles.terminalNode} ${styles.endNode} ${selectedId === 'end' ? styles.selectedEnd : ''}`}
                onClick={() => setSelectedId(selectedId === 'end' ? null : 'end')}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700 }}>END</span>
                  {maxExecutionTime != null && (
                    <span style={{
                      fontSize: '0.5rem',
                      background: 'rgba(255,255,255,0.25)',
                      padding: '1px 4px',
                      borderRadius: '3px',
                      fontWeight: 700,
                    }}>
                      {maxExecutionTime}s
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Floating Dropdown Insert Step Selection Menu */}
          {activeInsertZone && (
            <div
              className={styles.insertMenu}
              style={{
                position: 'absolute',
                left: activeInsertZone.x,
                top: activeInsertZone.y,
                transform: 'translateX(-50%)',
                zIndex: 100,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.insertMenuHeader}>Select Step Type</div>
              <div className={styles.insertMenuItems}>
                {Object.entries(STEP_META).map(([typeKey, meta]) => {
                  const type = typeKey as StepType;
                  const { Icon } = meta;
                  return (
                    <button
                      key={type}
                      className={styles.insertMenuItem}
                      onClick={() => {
                        const { id, branch } = activeInsertZone;
                        if (id === 'end') {
                          if (branch === 'true' || branch === 'false') {
                            addStepToBranch(type, branch);
                          } else {
                            addStep(type);
                          }
                        } else {
                          insertStepBefore(id, branch, type);
                        }
                        setActiveInsertZone(null);
                      }}
                      style={{
                        '--step-color-dim': `${meta.color}15`,
                        '--step-color': meta.color,
                      } as React.CSSProperties}
                    >
                      <Icon size={14} className={styles.insertMenuIcon} />
                      <span>{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Step Legend below canvas */}
        <div className={styles.legend}>
          <div className={styles.legendLabel}>Step Types Legend</div>
          <div className={styles.legendGrid}>
            {Object.entries(STEP_META).map(([typeKey, meta]) => {
              const type = typeKey as StepType;
              const { Icon } = meta;
              const description =
                type === 'wait' ? 'Pause execution for a set duration' :
                type === 'tool' ? 'Execute a registered API or code tool' :
                type === 'skill' ? 'Run an AI agent skill' :
                type === 'condition' ? 'Branch based on an expression' :
                type === 'callback' ? 'Wait for an external webhook callback' :
                type === 'emit_event' ? 'Publish an event to a connector' :
                type === 'wait_event' ? 'Pause until a correlated event arrives' :
                type === 'loop' ? 'Jump back to a previous step' : '';

              return (
                <div key={type} className={styles.legendItem}>
                  <div
                    className={styles.legendIconWrap}
                    style={{
                      background: `${meta.color}15`,
                      color: meta.color,
                    }}
                  >
                    <Icon size={12} strokeWidth={2.5} />
                  </div>
                  <div className={styles.legendText}>
                    <span className={styles.legendStepLabel}>{meta.label}</span>
                    <span className={styles.legendStepDesc}>{description}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Config panel (fixed, slides in from right) */}
      <StepConfigPanel
        step={selectedId === 'trigger' || selectedId === 'end' ? null : selectedStep}
        isTriggerSelected={selectedId === 'trigger'}
        isEndSelected={selectedId === 'end'}
        maxExecutionTime={maxExecutionTime}
        onChangeMaxExecutionTime={setMaxExecutionTime}
        triggerConfig={triggerConfig}
        onUpdateTrigger={(patch) => setTriggerConfig((prev: any) => ({ ...prev, ...patch }))}
        availableConnectors={availableConnectors}
        allSteps={steps}
        availableTools={availableTools}
        availableSkills={availableSkills}
        onUpdate={updateStep}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
